import { createAsyncThunk } from "@reduxjs/toolkit";

import includes from "lodash/includes";

import { AppState } from "@/redux/store/store";
import {
  getIdeas,
  getBrainstormer,
  postIdea,
  editIdea,
  postVote,
  deleteVote,
  getIdea,
  getComments,
  postReportIdea,
  postReportIdeaComment,
  getBrainstormers,
  getMentionSuggestions,
  postIdeaComment,
  deleteIdeaComment,
  deleteIdea,
  editIdeaComment,
  getUserBudgetAllocations,
  postBAContribution,
  deleteBAContribution,
  getCategories
} from "@/models";
import type { Brainstormer, Idea } from "@/types";
import { fetchAuthToken } from "@/helpers/fetch-auth-token";

export const fetchIdeas = createAsyncThunk(
  "brainstormers/fetchIdeas",
  async (
    params: Partial<{
      projectId: string;
      brainstormerId: string;
      pageSize: number | string;
      page: number | string;
      sortOrder: string;
      sortBy: string;
      search: string;
      categoryId: string | null;
      config: object;
    }>,
    { rejectWithValue, getState }
  ) => {
    const { token } = fetchAuthToken(getState());
    const { adapter } = getState() as AppState;

    const { baseUrl } = adapter.adapter;
    const {
      projectId,
      brainstormerId,
      pageSize,
      page,
      sortOrder = "desc",
      sortBy = "created_at",
      search,
      categoryId,
      config
    } = params;
    try {
      let filterQ: {
        sort: { [key: string]: string };
        search?: string;
        category_id?: string | null;
      } = { sort: { [sortBy]: sortOrder } };
      if (search) filterQ = { ...filterQ, search };
      if (categoryId) filterQ = { ...filterQ, category_id: categoryId };

      const response = await getIdeas(
        token,
        baseUrl,
        projectId,
        brainstormerId,
        {
          filterable: true,
          page_size: pageSize || 20,
          page,
          filters: filterQ
        },
        config
      );

      return response as unknown as {
        ideaList: Partial<Array<Idea>>;
        meta: { count: number };
        filters: {
          sort: object;
          search: string;
        };
        page: string | number;
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchBrainstormer = createAsyncThunk(
  "brainstormers/fetchBrainstormer",
  async (
    params: Partial<{
      projectId: string;
      brainstormerId: string;
      config: object;
    }>,
    { rejectWithValue, getState }
  ) => {
    const { token } = fetchAuthToken(getState());
    const { adapter } = getState() as AppState;

    const { baseUrl } = adapter.adapter;

    const { projectId, brainstormerId, config } = params;
    try {
      const response = await getBrainstormer(
        token,
        baseUrl,
        projectId,
        brainstormerId,
        {},
        config
      );

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchBrainstormers = createAsyncThunk(
  "brainstormers/fetchBrainstormers",
  async (
    params: Partial<{
      projectId: string;
      layout: "Ideas" | "BudgetAllocator";
      config: object;
    }>,
    { rejectWithValue, getState }
  ) => {
    const { token } = fetchAuthToken(getState());
    const { adapter, project } = getState() as AppState;

    const { baseUrl } = adapter.adapter;

    const { roleInCurrentContext } = project.project;
    const { projectId, layout, config } = params;

    try {
      const response = (await getBrainstormers(
        token,
        baseUrl,
        projectId,
        {},
        config
      )) as Array<
        Partial<
          Brainstormer & {
            ideas: { meta: { count: number }; ideaList: Array<Partial<Idea>> };
          }
        >
      >;

      // Brainstormer backs both the Ideas and BudgetAllocator layouts, and
      // `layout` is nullable for rows that predate that column (no backfill
      // ran). Only BudgetAllocator gets an exact match; every other value
      // (including undefined, which callers that just want ideas pass) is
      // treated as Ideas so legacy rows aren't dropped from that banner.
      let brainstormers =
        layout === "BudgetAllocator"
          ? response.filter((res) => res.layout === "BudgetAllocator")
          : response.filter((res) => res.layout !== "BudgetAllocator");

      if (roleInCurrentContext === "participant") {
        brainstormers = brainstormers.filter((res) =>
          includes(["published", "archived"], res.state)
        );
      }

      for (let i = 0; i < brainstormers.length; i++) {
        const ideas = (await getIdeas(
          token,
          baseUrl,
          projectId,
          brainstormers[i].id,
          {
            filterable: true,
            page: 1,
            page_size: 5,
            filters: { sort: { created_at: "desc" } }
          },
          config
        )) as unknown as {
          meta: { count: number };
          ideaList: Array<Partial<Idea>>;
        };

        brainstormers[i].ideas = ideas;
      }

      return brainstormers;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const saveIdea = createAsyncThunk(
  "brainstormers/saveIdea",
  async (
    params: Partial<{
      name: string;
      description: string;
      mediaUrl: string;
      backgroundColourId?: number;
      baItemConfigAttributes?: { unitCap?: number };
      categoryId?: string | null;
    }>,
    { rejectWithValue, getState }
  ) => {
    const { token } = fetchAuthToken(getState());
    const { adapter, brainstormers, project } = getState() as AppState;

    const { baseUrl } = adapter.adapter;
    const projectId = project.project?.id;
    const brainstormerId = brainstormers.brainstormer?.id;
    const {
      name,
      description,
      mediaUrl,
      backgroundColourId,
      baItemConfigAttributes,
      categoryId
    } = params;

    try {
      const response = await postIdea(
        token,
        baseUrl,
        projectId,
        brainstormerId,
        {
          name,
          description: description || "",
          mediaUrl: mediaUrl || "",
          backgroundColourId: backgroundColourId || null,
          ...(baItemConfigAttributes?.unitCap
            ? { baItemConfigAttributes }
            : {}),
          categoryId: categoryId || null
        }
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateOneIdea = createAsyncThunk(
  "brainstormers/updateOneIdea",
  async (
    params: Partial<{
      ideaId: string;
      name: string;
      description: string;
      mediaUrl: string;
      backgroundColourId?: number;
      baItemConfigAttributes?: { unitCap?: number };
      categoryId?: string | null;
    }>,
    { rejectWithValue, getState }
  ) => {
    const { token } = fetchAuthToken(getState());

    const { adapter, brainstormers, project } = getState() as AppState;

    const { baseUrl } = adapter.adapter;
    const projectId = project.project?.id;
    const brainstormerId = brainstormers.brainstormer?.id;
    const {
      ideaId,
      name,
      description,
      mediaUrl,
      backgroundColourId,
      baItemConfigAttributes,
      categoryId
    } = params;

    try {
      const response = await editIdea(
        token,
        baseUrl,
        projectId,
        brainstormerId,
        ideaId,
        {
          name,
          description: description || "",
          mediaUrl: mediaUrl || "",

          backgroundColourId: backgroundColourId || null,
          ...(baItemConfigAttributes?.unitCap
            ? { baItemConfigAttributes }
            : {}),
          categoryId: categoryId || null
        }
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const refreshOneIdea = createAsyncThunk(
  "brainstormers/refreshOneIdea",
  async (
    params: Partial<{
      ideaId: string;
    }>,
    { rejectWithValue, getState }
  ) => {
    const { token } = fetchAuthToken(getState());

    const { adapter, brainstormers, project } = getState() as AppState;
    const { brainstormer } = brainstormers;
    const { id: projectId } = project?.project || {};

    const { baseUrl } = adapter.adapter;
    const { ideaId } = params;

    try {
      const response = await getIdea(
        token,
        baseUrl,
        projectId,
        brainstormer?.id,
        ideaId
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const saveVote = createAsyncThunk(
  "brainstormers/saveVote",
  async (
    params: Partial<{
      projectId: string;
      brainstormerId: string;
      ideaId: string;
    }>,
    { rejectWithValue, getState }
  ) => {
    const { token } = fetchAuthToken(getState());

    const { adapter } = getState() as AppState;

    const { baseUrl } = adapter.adapter;
    const { projectId, brainstormerId, ideaId } = params;

    try {
      const response = await postVote(
        token,
        baseUrl,
        projectId,
        brainstormerId,
        ideaId
      );

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchIdea = createAsyncThunk(
  "brainstormers/fetchIdea",
  async (
    params: Partial<{
      projectId: string;
      brainstormerId: string;
      ideaId: string;
      config: object;
    }>,
    { rejectWithValue, getState }
  ) => {
    const { token } = fetchAuthToken(getState());

    const { adapter } = getState() as AppState;

    const { baseUrl } = adapter.adapter;
    const { projectId, brainstormerId, ideaId, config } = params;

    try {
      const response = await getIdea(
        token,
        baseUrl,
        projectId,
        brainstormerId,
        ideaId,
        {},
        config
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchComments = createAsyncThunk(
  "brainstormers/fetchComments",
  async (
    params: Partial<{
      projectId: string;
      brainstormerId: string;
      ideaId: string;
      sortOrder: string;
      sortBy: string;
      pageSize: number;
      page: number;
      config: object;
    }>,
    { rejectWithValue, getState }
  ) => {
    const { token } = fetchAuthToken(getState());

    const { adapter } = getState() as AppState;

    const { baseUrl } = adapter.adapter;
    const {
      projectId,
      brainstormerId,
      ideaId,
      sortOrder = "desc",
      sortBy = "created_at",
      pageSize = 100,
      page = 1,
      config
    } = params;

    try {
      const response = await getComments(
        token,
        baseUrl,
        projectId,
        brainstormerId,
        ideaId,
        {
          filterable: true,
          filters: { sort: { [sortBy]: sortOrder } },
          page_size: pageSize,
          page: page
        },
        config
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const reportIdea = createAsyncThunk(
  "brainstormers/reportIdea",

  async (
    params: Partial<{
      ideaId: string;
    }>,
    { rejectWithValue, getState }
  ) => {
    const { token } = fetchAuthToken(getState());

    const { adapter, brainstormers, project } = getState() as AppState;
    const projectId = project?.project?.id;
    const brainstormerId = brainstormers?.brainstormer?.id;

    const { baseUrl } = adapter.adapter;
    const { ideaId } = params;

    try {
      const response = await postReportIdea(
        token,
        baseUrl,
        projectId,
        brainstormerId,
        ideaId
      );

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const reportIdeaComment = createAsyncThunk(
  "brainstormers/reportIdeaComment",
  async (
    params: Partial<{
      ideaId: string;
      commentId: string;
    }>,
    { rejectWithValue, getState }
  ) => {
    const { token } = fetchAuthToken(getState());

    const { adapter, brainstormers, project } = getState() as AppState;
    const projectId = project?.project?.id;
    const brainstormerId = brainstormers?.brainstormer?.id;

    const { baseUrl } = adapter.adapter;
    const { ideaId, commentId } = params;

    try {
      const response = await postReportIdeaComment(
        token,
        baseUrl,
        projectId,
        brainstormerId,
        ideaId,
        commentId
      );

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const removeVote = createAsyncThunk(
  "brainstormers/deleteVote",
  async (
    params: Partial<{
      projectId: string;
      brainstormerId: string;
      ideaId: string;
    }>,
    { rejectWithValue, getState }
  ) => {
    const { token } = fetchAuthToken(getState());

    const { adapter } = getState() as AppState;

    const { baseUrl } = adapter.adapter;
    const { projectId, brainstormerId, ideaId } = params;

    try {
      const response = await deleteVote(
        token,
        baseUrl,
        projectId,
        brainstormerId,
        ideaId
      );

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchMentionSuggestions = createAsyncThunk(
  "brainstormers/fetchMentionSuggestions",
  async (
    params: Partial<{
      projectId: string;
      brainstormerId: string;
      ideaId: string;
    }>,
    { rejectWithValue, getState }
  ) => {
    const { token } = fetchAuthToken(getState());
    const { adapter } = getState() as AppState;

    const { baseUrl } = adapter.adapter;
    const { projectId, brainstormerId, ideaId } = params;

    try {
      const response = await getMentionSuggestions(
        token,
        baseUrl,
        projectId,
        brainstormerId,
        ideaId
      );

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const postComment = createAsyncThunk(
  "brainstormers/postIdeaComment",
  async (
    params: Partial<{ comment: string; mentions: Array<unknown> }>,
    { rejectWithValue, getState }
  ) => {
    const { token } = fetchAuthToken(getState());
    const { adapter, brainstormers, project } = getState() as AppState;
    const { baseUrl } = adapter.adapter;
    const projectId = project.project?.id;
    const brainstormerId = brainstormers?.brainstormer?.id;
    const ideaId = brainstormers?.idea?.id;

    const { comment, mentions } = params;

    try {
      const response = await postIdeaComment(
        token,
        baseUrl,
        projectId,
        brainstormerId,
        ideaId,
        {
          comment,
          mentions
        }
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
export const editComment = createAsyncThunk(
  "brainstormers/editComment",
  async (
    params: Partial<{
      commentId: string;
      comment: string;
      mentions: Array<unknown>;
    }>,
    { rejectWithValue, getState }
  ) => {
    const { token } = fetchAuthToken(getState());

    const { adapter, brainstormers, project } = getState() as AppState;
    const { baseUrl } = adapter.adapter;
    const projectId = project.project?.id;
    const brainstormerId = brainstormers?.brainstormer?.id;
    const ideaId = brainstormers?.idea?.id;
    const { comment, commentId, mentions } = params;
    try {
      const response = await editIdeaComment(
        token,
        baseUrl,
        projectId,
        brainstormerId,
        ideaId,
        commentId,
        { comment, mentions }
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const removeIdeaComment = createAsyncThunk(
  "brainstormers/deleteIdeaComment",
  async (
    params: Partial<{
      projectId: string;
      brainstormerId: string;
      ideaId: string;
      commentId: string;
    }>,
    { rejectWithValue, getState }
  ) => {
    const { token } = fetchAuthToken(getState());
    const { adapter } = getState() as AppState;
    const { baseUrl } = adapter.adapter;
    const { projectId, brainstormerId, ideaId, commentId } = params;

    try {
      const response = await deleteIdeaComment(
        token,
        baseUrl,
        projectId,
        brainstormerId,
        ideaId,
        commentId
      );

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
export const removeIdea = createAsyncThunk(
  "brainstormers/deleteIdea",
  async (
    params: Partial<{
      projectId: string;
      brainstormerId: string;
      ideaId: string;
    }>,
    { rejectWithValue, getState }
  ) => {
    const { token } = fetchAuthToken(getState());
    const { adapter } = getState() as AppState;
    const { baseUrl } = adapter.adapter;
    const { projectId, brainstormerId, ideaId } = params;

    try {
      const response = await deleteIdea(
        token,
        baseUrl,
        projectId,
        brainstormerId,
        ideaId
      );

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchUserBudgetAllocations = createAsyncThunk(
  "brainstormers/fetchUserBudgetAllocations",
  async (_, { rejectWithValue, getState }) => {
    const { token } = fetchAuthToken(getState());
    const { adapter, brainstormers, project } = getState() as AppState;

    const { baseUrl } = adapter.adapter;
    const projectId = project.project?.id;
    const brainstormerId = brainstormers?.brainstormer?.id;

    try {
      const response = (await getUserBudgetAllocations(
        token,
        baseUrl,
        projectId,
        brainstormerId
      )) as Record<string, number>;

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const saveBAContribution = createAsyncThunk<
  unknown,
  {
    ideaId: string;
    unitValue: number;
  },
  {
    rejectValue: any;
  }
>(
  "brainstormers/saveBAContribution",
  async ({ ideaId, unitValue }, { rejectWithValue, getState }) => {
    const { token } = fetchAuthToken(getState());
    const { adapter, brainstormers, project } = getState() as AppState;

    const { baseUrl } = adapter.adapter;
    const projectId = project.project?.id;
    const brainstormerId = brainstormers?.brainstormer?.id;

    try {
      const response = await postBAContribution(
        token,
        baseUrl,
        projectId,
        brainstormerId,
        ideaId,
        { unitValue }
      );

      return { ...response, ideaId };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const removeBAContribution = createAsyncThunk<
  unknown,
  {
    ideaId: string;
  },
  {
    rejectValue: any;
  }
>(
  "brainstormers/removeBAContribution",
  async ({ ideaId }, { rejectWithValue, getState }) => {
    const { token } = fetchAuthToken(getState());
    const { adapter, brainstormers, project } = getState() as AppState;
    const { baseUrl } = adapter.adapter;
    const projectId = project.project?.id;
    const brainstormerId = brainstormers?.brainstormer?.id;

    try {
      const response = await deleteBAContribution(
        token,
        baseUrl,
        projectId,
        brainstormerId,
        ideaId
      );

      return { ...response, ideaId };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchCategories = createAsyncThunk(
  "brainstormers/fetchCategories",
  async (
    params: Partial<{
      config: object;
      projectId: string;
      brainstormerId: string;
    }>,
    { rejectWithValue, getState }
  ) => {
    const { adapter } = getState() as AppState;
    const { token } = fetchAuthToken(getState());
    const { baseUrl } = adapter.adapter;
    const { config, projectId, brainstormerId } = params;

    try {
      const response = await getCategories(
        token,
        baseUrl,
        projectId,
        brainstormerId,
        {},
        config
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
